import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import _ from 'lodash';
import { Observable, forkJoin, of } from 'rxjs';
import { StartEditingBrickSettings, StopEditingBrickSettings } from 'src/app/store/dashboard/brick-wall/brick-instances/brick-instances.actions';
import { BrickInstancesSelectors } from 'src/app/store/dashboard/brick-wall/brick-instances/brick-instances.selectors';
import { IBrickDescription } from 'src/app/store/dashboard/brick-wall/brick-instances/brick-instances.state';
import { BrickSettingsChangedByServer } from 'src/app/store/dashboard/brick-wall/brick-settings/brick-settings.actions';
import { SaveAllChangedBrickSettings } from 'src/app/store/dashboard/brick-wall/brick-settings/common-brick-settings/common-brick-settings.actions';
import { CommonBrickSettingsSelectors } from 'src/app/store/dashboard/brick-wall/brick-settings/common-brick-settings/common-brick-settings.selectors';
import { AbstractBrickSettingRemoteDto } from 'src/generated/signalR/abstract-brick-setting-remote-dto';
import { CommonBrickSettingRemoteDto } from 'src/generated/signalR/common-brick-setting-remote-dto';

export interface IBrickSettingStateModel<TSpecificBrickSettings> {
   specificBrickSettings: SpecificBrickSettingsModelContainer<TSpecificBrickSettings>;
   settingsFromServerReceived?: boolean;
}

export interface SpecificBrickSettingsModelContainer<TSpecificBrickSettings> {
   brickSettings: { [brickId: string]: TSpecificBrickSettings };
   brickSettingsEditMode: { [brickId: string]: TSpecificBrickSettings };
}

export function SpecificBrickSettingsStateBase<TSpecificBrickSettings>(getDefaultSpecificBrickSettingFn: () => TSpecificBrickSettings) {
   // tslint:disable-next-line:no-shadowed-variable
   abstract class SpecificBrickSettingsStateBase {
      protected constructor(protected store: Store) {}

      @Selector([CommonBrickSettingsSelectors.getBrickSettingModelFn])
      public static getSettingModelFn(
         getBrickSettingModelFn
      ): (state: any, brickId: string, ignoreEditModeValue: boolean) => TSpecificBrickSettings & AbstractBrickSettingRemoteDto {
         return (state: any, brickId: string, ignoreEditModeValues: boolean = false) => {
            const specificSettings = this.getSettingModelOrDefault(state, brickId, true, ignoreEditModeValues);
            const basicSettings: CommonBrickSettingRemoteDto = getBrickSettingModelFn(brickId, ignoreEditModeValues);

            return {
               ...specificSettings,
               ...(<AbstractBrickSettingRemoteDto>{
                  commonBrickSettingRemoteDto: basicSettings,
               }),
            };
         };
      }

      protected static getSettingModelOrDefault(
         state: IBrickSettingStateModel<TSpecificBrickSettings>,
         brickId: string,
         getDefaultIfNotAvailable: boolean = true,
         ignoreEditModeValues: boolean = false
      ): TSpecificBrickSettings {
         let settingModel: TSpecificBrickSettings;
         if (_.isNil(state.specificBrickSettings.brickSettingsEditMode) || ignoreEditModeValues) {
            settingModel = state.specificBrickSettings.brickSettings[brickId];
         } else {
            settingModel = state.specificBrickSettings.brickSettingsEditMode[brickId];
         }
         if (_.isNil(settingModel) && getDefaultIfNotAvailable) {
            settingModel = getDefaultSpecificBrickSettingFn();
         }
         return settingModel;
      }

      @Action(StartEditingBrickSettings)
      public enableEditModeForBrickSettings(ctx: StateContext<IBrickSettingStateModel<TSpecificBrickSettings>>, action: StartEditingBrickSettings) {
         if (!_.isNil(ctx.getState().specificBrickSettings.brickSettingsEditMode)) {
            return;
         }
         ctx.setState(
            patch({
               specificBrickSettings: patch({
                  brickSettingsEditMode: _.cloneDeep(ctx.getState().specificBrickSettings.brickSettings),
               }),
            })
         );
      }

      @Action(StopEditingBrickSettings)
      public disableEditModeForBrickSettings(ctx: StateContext<IBrickSettingStateModel<TSpecificBrickSettings>>, action: StopEditingBrickSettings) {
         ctx.setState(
            patch({
               specificBrickSettings: patch({
                  brickSettingsEditMode: null,
               }),
            })
         );
      }

      @Action(SaveAllChangedBrickSettings)
      public saveAllChangedBrickSettings(ctx: StateContext<IBrickSettingStateModel<TSpecificBrickSettings>>, action: SaveAllChangedBrickSettings) {
         const observables = action.brickIds.map((brickInstanceId) => this.saveBrickSettings(ctx.getState(), brickInstanceId)).filter((x) => !_.isNil(x));
         if (_.isEmpty(observables)) {
            return;
         }

         return forkJoin(observables);
      }

      @Action(BrickSettingsChangedByServer)
      @ImmutableContext()
      public brickSettingsChangedByServer(ctx: StateContext<IBrickSettingStateModel<TSpecificBrickSettings>>, action: BrickSettingsChangedByServer) {
         ctx.setState((state: IBrickSettingStateModel<TSpecificBrickSettings>) => {
            state.settingsFromServerReceived = true;
            _.forEach(action.brickSettings, (value, key) => {
               const brickDescription: IBrickDescription = this.store.selectSnapshot(BrickInstancesSelectors.brickSettingDescriptionFn)(key);

               if (
                  _.isNil(brickDescription) ||
                  _.isNil(brickDescription.definition) ||
                  _.isNil(brickDescription.definition.settingStateHandler) ||
                  !(this instanceof brickDescription.definition.settingStateHandler)
               ) {
                  // another specific brick setting state should be responsible for this action!
                  return true; // continue
               }
               const valueCopy = _.cloneDeep(value);
               delete valueCopy.commonBrickSettingRemoteDto;

               state.specificBrickSettings.brickSettings[key] = this.onSettingsUpdatedByServer(
                  brickDescription.brickInstance.brickInstanceId,
                  valueCopy as any
               );
            });
            return state;
         });
      }

      protected onSettingsUpdatedByServer(brickInstanceId: string, settings: TSpecificBrickSettings): TSpecificBrickSettings {
         return settings;
      }

      private saveBrickSettings(state: IBrickSettingStateModel<TSpecificBrickSettings>, brickInstanceId: string): Observable<any> {
         const brickDescription: IBrickDescription = this.store.selectSnapshot(BrickInstancesSelectors.brickDescriptionFn)(brickInstanceId);

         if (_.isNil(brickDescription.definition.settingStateHandler) || !(this instanceof brickDescription.definition.settingStateHandler)) {
            // another specific brick setting state should be responsible for this action!
            return;
         }

         const brickSetting = this.onPrepareBrickSettingsToSave(
            brickInstanceId,
            this.store.selectSnapshot(SpecificBrickSettingsStateBase.getSettingModelFn)(state, brickInstanceId, false)
         );
         const brickSettingUnchanged = this.store.selectSnapshot(SpecificBrickSettingsStateBase.getSettingModelFn)(state, brickInstanceId, true);

         if (_.isNil(brickSetting) || _.isEqual(brickSetting, brickSettingUnchanged)) {
            return of(null);
         }

         return this.doSaveSpecificSettings(brickInstanceId, brickSetting);
      }

      protected abstract doSaveSpecificSettings(brickInstanceId: string, settings: TSpecificBrickSettings & AbstractBrickSettingRemoteDto): Observable<any>;

      protected onPrepareBrickSettingsToSave(
         brickInstanceId: string,
         settingsToSave: TSpecificBrickSettings & AbstractBrickSettingRemoteDto
      ): TSpecificBrickSettings & AbstractBrickSettingRemoteDto {
         return settingsToSave;
      }
   }

   return SpecificBrickSettingsStateBase;
}
